import { useState, type FormEvent } from 'react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Input,
  Pill,
  Select,
  Table,
  tableCell,
} from '../../components/ui';
import { useActivity, useActivityFilters, useCurrentUser } from '../../hooks';
import { teamLabel } from '../../lib/team';
import { formatDateTime } from '../tts/format';
import styles from './AdminActivityPage.module.css';

const PAGE_SIZE = 50;

interface Filters {
  team: string;
  user: string;
  action: string;
  date_from: string;
  date_to: string;
}

const NO_FILTERS: Filters = { team: '', user: '', action: '', date_from: '', date_to: '' };

export function AdminActivityPage() {
  const { isAdmin, isLoading: meLoading } = useCurrentUser();

  // The controls edit a draft; only "Применить" turns it into the filters that are queried.
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [offset, setOffset] = useState(0);

  const options = useActivityFilters(isAdmin);
  const list = useActivity({ ...filters, limit: PAGE_SIZE, offset }, isAdmin);

  if (meLoading) {
    return <div className={styles.hint}>Проверяем доступ…</div>;
  }

  if (!isAdmin) {
    return (
      <div className={styles.noAccess}>
        <h2>Нет доступа</h2>
        <p>Эта страница доступна только администраторам.</p>
      </div>
    );
  }

  const change = (patch: Partial<Filters>) => setDraft((current) => ({ ...current, ...patch }));
  const apply = (event?: FormEvent) => {
    event?.preventDefault();
    setFilters(draft);
    setOffset(0);
  };
  const reset = () => {
    setDraft(NO_FILTERS);
    setFilters(NO_FILTERS);
    setOffset(0);
  };
  const filtered = Object.values(filters).some(Boolean);
  const pending = JSON.stringify(draft) !== JSON.stringify(filters);

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <h1 className={styles.title}>Активность</h1>
        <span className={styles.subtitle}>
          Журнал действий сотрудников. Записи нельзя изменить или удалить.
        </span>
      </header>

      <Card>
        <CardHead title="Фильтры" />
        <CardBody pad>
          <form className={styles.filters} onSubmit={apply}>
            <Select
              value={draft.team}
              onChange={(e) => change({ team: e.target.value })}
              aria-label="Команда"
            >
              <option value="">Все команды</option>
              {(options.data?.teams ?? []).map((team) => (
                <option key={team} value={team}>
                  {teamLabel(team)}
                </option>
              ))}
            </Select>
            <Select
              value={draft.user}
              onChange={(e) => change({ user: e.target.value })}
              aria-label="Сотрудник"
            >
              <option value="">Все сотрудники</option>
              {(options.data?.users ?? []).map((login) => (
                <option key={login} value={login}>
                  {login}
                </option>
              ))}
            </Select>
            <Select
              value={draft.action}
              onChange={(e) => change({ action: e.target.value })}
              aria-label="Действие"
            >
              <option value="">Все действия</option>
              {(options.data?.actions ?? []).map((option) => (
                <option key={option.action} value={option.action}>
                  {option.label}
                </option>
              ))}
            </Select>
            <label className={styles.dateField}>
              с
              <Input
                type="date"
                value={draft.date_from}
                max={draft.date_to || undefined}
                onChange={(e) => change({ date_from: e.target.value })}
                aria-label="С даты"
              />
            </label>
            <label className={styles.dateField}>
              по
              <Input
                type="date"
                value={draft.date_to}
                min={draft.date_from || undefined}
                onChange={(e) => change({ date_to: e.target.value })}
                aria-label="По дату"
              />
            </label>
            <Button type="submit" size="sm" variant="primary" disabled={!pending}>
              Применить
            </Button>
            <Button size="sm" disabled={!filtered && !pending} onClick={reset}>
              Сбросить
            </Button>
          </form>
          <div className={styles.note}>Даты считаются по UTC.</div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="Записи"
          hint={<Pill variant="solid">{total}</Pill>}
        />
        <CardBody>
          {list.isPending && <div className={styles.hint}>Загружаем…</div>}
          {list.error && <ErrorBanner error={list.error} onRetry={() => void list.refetch()} />}

          {list.data && items.length === 0 && (
            <EmptyState
              title="Записей нет"
              description={filtered ? 'По этим фильтрам ничего не нашлось.' : 'Журнал пуст.'}
            />
          )}

          {items.length > 0 && (
            <Table bare>
              <thead>
                <tr>
                  <th>Время</th>
                  <th>Сотрудник</th>
                  <th>Команда</th>
                  <th>Действие</th>
                  <th>Объект</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td className={tableCell.mono}>{formatDateTime(entry.created_at)}</td>
                    <td>
                      {entry.login ?? '—'}
                      {entry.job && <span className={styles.job}>{entry.job}</span>}
                    </td>
                    <td>{teamLabel(entry.team, '—')}</td>
                    <td title={entry.action}>{entry.action_label}</td>
                    <td title={entry.entity_id ?? undefined}>
                      <span className={styles.entityType}>{entry.entity_label}</span>{' '}
                      {entry.subject}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {total > PAGE_SIZE && (
            <div className={styles.pager}>
              <Button
                size="sm"
                disabled={offset === 0 || list.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Назад
              </Button>
              <span>
                {from}–{to} из {total}
              </span>
              <Button
                size="sm"
                disabled={offset + PAGE_SIZE >= total || list.isFetching}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Дальше
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
